import React, { useCallback, useContext, useEffect, useState } from "react";
import { AiAssistContext } from "../AiAssist.context";
import { EonUiButton, EonUiPreloaderSquares, EonUiTagPill, EonUiTagPillGroup, EonUiTooltip } from "@eon-ui/eon-ui-components-react";
import TypingEffect from "../utils/typing-effect/TypingEffect";
import { DecisionNode, getDecisionTree, getUserCommand } from "./decisionTree";
import useSearchStore from "../../../store/searchStore";
import { CreateIncidentInput, CreateIncidentResponse } from "../../feedback/feedback.createIncident.component";
import { raiseIncident } from "../../../services/raiseIncident";
import { useTranslation } from "@me8eon/translation";
import { Icon } from "@me8eon/icons";
import RequestCallback from "../../request-callback/RequestCallback";
import RequestCallbackResult from "../../request-callback/RequestCallbackResult";
import { AiActionByCustomType } from "../../ai/ai.actions";
import { getFrom } from "../../../util/map";
import { Message } from "@me8eon/messages";
import { MessageWithActionAndData } from "@me8eon/sessions_messages";
import { QuickActionType } from "../../../modules/AiWidgets/widget.types";
import { ExpandingInput } from '../../../shared/ui/molecules/ExpandingInput';
import { useFeedback } from "@me8eon/feedback";
import { useUserData } from "@me8eon/react_authentication";

export const Options: React.FC<{ data: any, isButton?: boolean, isDisabled?: boolean }> = ({
    data,
    isButton = false,
    isDisabled = false
}) => {
    const t = useTranslation();
    const [disabled, setDisabled] = useState<boolean>(isDisabled);

    useEffect(() => {
        setDisabled(isDisabled);
    }, [isDisabled])

    const buttonGroupRef = React.useRef<HTMLDivElement>(null);

    return (
        <div className="bot-content" ref={buttonGroupRef}>
            {isButton ? (<div className="common-button-wrapper" style={{ margin: 0 }}>
                {data?.options?.map(((option: any, index: number) => (
                    <EonUiButton
                        key={index}
                        scheme={option.scheme}
                        text={t(option.label)}
                        // icon={option.icon}
                        size="small"
                        rank={index === 0 ? "primary" : "secondary"}
                        onClick={() => {
                            if (disabled || option.disabled) return;
                            setDisabled(true);
                            setTimeout(() => {
                                buttonGroupRef.current?.scrollIntoView({ behavior: 'smooth' });
                            }, 100);
                            if (data.callback) data.callback(option);
                        }}
                        className="citation-item"
                        disabled={disabled || option.disabled}
                        style={{ margin: "8px 0" }}
                    />
                )))}
            </div>
            ) : (
                <EonUiTagPillGroup>
                    {data?.options?.map(((option: any, index: number) => (
                        <EonUiTagPill
                            key={index}
                            scheme={option.scheme}
                            text={t(option.label)}
                            icon={option.icon}
                            onClick={() => {
                                if (disabled || option.disabled) return;
                                setDisabled(true);
                                setTimeout(() => {
                                    buttonGroupRef.current?.scrollIntoView({ behavior: 'smooth' });
                                }, 100);
                                if (data.callback) data.callback(option);
                            }}
                            className="citation-item"
                            isDisabled={disabled || option.disabled}
                        ></EonUiTagPill>
                    )))}
                </EonUiTagPillGroup>)}
        </div>
    );
};

const CreateIncident: React.FC<{ data: any }> = ({
    data
}) => {
    const t = useTranslation();
    // const [ messages ] = useMessages();
    const { incidentText, isIncidentTextPending } = data;
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isFileError, setIsFileError] = useState<boolean>(false);
    const [incText, setIncText] = useState<string>(incidentText || "");
    const { incidentFiles, setIncidentFiles, contactNumber, handleAiQuery } = useContext(AiAssistContext);
    const [errorField, setErrorField] = useState<string[]>([]);
    // const lastMessage = messages.filter(item => item.action === "user-query")?.pop()?.content;
    // const lastMessageContent = typeof lastMessage === "string" ? lastMessage : (Array.isArray(lastMessage) && lastMessage.length > 0 && typeof lastMessage[0] === "object" && "text" in lastMessage[0] ? lastMessage[0].text : "");

    const composed = (...args: any) => {
        setIsSubmitting(true);
        data.callback?.(...args);
    };

    const isValidPhoneNumber = (value: string): boolean => {
        const trimmed = value.trim();
        const phoneFormatRegex = /^\+?\d(?:-?\d)*$/;
        if (!phoneFormatRegex.test(trimmed)) return false;
        const digitsOnly = trimmed.replace(/\D/g, "");
        return digitsOnly.length >= 6 && digitsOnly.length <= 15;
    };

    const isValidIncidentText = (value: string): boolean => {
        return !!value && !!value.trim();
    };

    const checkContactErr = (contact: string) => {
        const contactHasError = !isValidPhoneNumber(contact);
        setErrorField(prev => {
            if (contactHasError) {
                return prev.includes("contact") ? prev : [...prev, "contact"];
            }
            return prev.filter((f) => f !== "contact");
        });
        return contactHasError;
    };

    const checkIncErr = (inc: string) => {
        const incHasError = !isValidIncidentText(inc);
        setErrorField(prev => {
            if (incHasError) {
                return prev.includes("incident") ? prev : [...prev, "incident"];
            }
            return prev.filter((f) => f !== "incident");
        });
        return incHasError;
    };

    const validateFields = (): boolean => {
        const isIncInvalid = checkIncErr(incText);
        const isContactInvalid = checkContactErr(contactNumber);
        return isIncInvalid || isContactInvalid;
    }

    const setIncTextFunc = useCallback((value: string) => {
        setIncText(value);
        data.incidentText = value;
        data.setIncidentText?.(value);
        checkIncErr(value);
        checkContactErr(contactNumber || "");
    }, [contactNumber]);

    const setContactFunc = useCallback((value: string) => {
        checkContactErr(value);
        checkIncErr(incText || "");
    }, [incText]);

    useEffect(() => {
        setIncText(incidentText || "");
    }, [incidentText]);

    const hasValidationError = !isValidIncidentText(incText) || !isValidPhoneNumber(contactNumber || "");

    // intercept option selection for this create-incident node so we can validate before proceeding
    data.onOptionSelect = (option: any) => {
        if (option && option.nextNodeId) {
            const inValid = validateFields();
            if (inValid) return;
            // proceed with default action
            handleAiQuery(getUserCommand(option.nextNodeId), "quick-actions", { incidentText: incText });
            if (data.callback) data.callback(option);
        }
    }

    const handleFile = () => {
        setIsFileError(false);
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/jpeg,image/png,image/jpg';
        input.onchange = (e: Event) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                if (file.size > maxSize) {
                    setIsFileError(true);
                    return;
                }
                // Convert file to base64 and store in state
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = (reader.result as string).split(',')[1];
                    setIncidentFiles([...incidentFiles, {
                        file_name: file.name,
                        size: file.size,
                        file_data: base64String
                    }]);
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    }

    return isIncidentTextPending ? (
        <EonUiPreloaderSquares className="preloader" />
    ) : (
        <>
            {/* {quickActionType &&  && <div className="non-it-warning">
                <div>{t("chatBot.quickActions.state.ticketNonITWarning")}</div>
                <div>{t("chatBot.quickActions.state.ticketNonITWarningQuestion")} <span className="question">&quot;{lastMessageContent}&quot;</span></div>
                <div><span className="recommendation">{t("chatBot.quickActions.state.ticketNonITWarningRecommendationLabel")} </span><span className="recommendation-text">{t("chatBot.quickActions.state.ticketNonITWarningRecommendationText")}</span></div>
            </div>} */}
            <div className="incident-box-wrapper"><CreateIncidentInput isEditIncident={true} setContactFunc={setContactFunc} disabled={isSubmitting} setIncTextFunc={setIncTextFunc} incidentInvalid={errorField.includes("incident")} contactInvalid={errorField.includes("contact")} incidentText={incText} isIncidentTextPending={isIncidentTextPending} /></div>
            <div className="ticket-file-attachment">
                <div className="ticket-file-attachment--label">
                    <span>{t("chatBot.quickActions.state.ticketAttachment")}</span>
                    <div className="ticket-file-attachment--tooltip-icon">
                        <Icon name="info_small" style={{ cursor: "pointer" }} />
                        <EonUiTooltip visibility="onHover" placement="top" size="small" style={{ padding: 0, borderRadius: "8px", overflow: "hidden" }}>
                            <div className="ticket-file-attachment--tooltip-container">
                                <span>{t("chatBot.quickActions.state.ticketAttachmentTooltipInstruction")}</span>
                                <span>{t("chatBot.quickActions.state.ticketAttachmentTooltipWarning")}</span>
                            </div>
                        </EonUiTooltip>
                    </div>
                </div>
                <div
                    className={`ticket-file-attachment--attach ${(isSubmitting || incidentFiles.length > 0) ? "ticket-file-attachment--attach-disabled" : ""}`}
                    onClick={() => { if (!isSubmitting && incidentFiles.length === 0) handleFile(); }}
                >
                    <Icon name="attachment" /> <span>{t("chatBot.quickActions.state.ticketAttachmentLabel")}</span>
                </div>
                {incidentFiles.length > 0 && <>
                    <span className="ticket-file-attachment--files-list--title">{t("chatBot.quickActions.state.ticketAttachmentTitle")}</span>
                    <div className="ticket-file-attachment--files-list">
                        {incidentFiles.map((file, index) => (
                            <div className="ticket-file-attachment--files-list--file-name" key={index}>
                                <span>{file.file_name} </span>
                                <EonUiTooltip visibility="onHover" placement="top" size="small" style={{ padding: 0, borderRadius: "8px", overflow: "hidden" }}>
                                    <img src={`data:image/*;base64,${file.file_data}`} alt={file.file_name} style={{ maxWidth: "200px", maxHeight: "200px" }} />
                                </EonUiTooltip>
                                {!isSubmitting && <span style={{ cursor: "pointer" }} onClick={() => { setIncidentFiles(incidentFiles.filter((f, i) => i !== index)) }}><Icon name="remove" /></span>}
                            </div>
                        ))}
                    </div>
                </>}
                {isFileError && (
                    <p className="ticket-file-attachment--error">
                        {t("chatBot.quickActions.state.ticketAttachmentError")} &nbsp;
                        <span onClick={() => { handleFile() }}>{t("chatBot.quickActions.state.ticketAttachmentErrorAction")}</span>
                    </p>
                )}
            </div>
            <Options data={{ ...data, callback: composed }} isButton={true} isDisabled={hasValidationError || isSubmitting} />
        </>
    );
};

const CreateIncidentResult: React.FC<{ data: any }> = ({
    data
}) => {
    const t = useTranslation();
    const { incidentText } = data;
    const [incidentResponse, setIncidentResponse] = useState<any>();
    const [respLoading, setRespLoading] = useState(false);
    const { incidentFiles, setIncidentFiles, contactNumber } = useContext(AiAssistContext);
    let lastMessageContent = null;
    lastMessageContent = incidentText;

    useEffect(() => {
        const createIncidentApi = async () => {
            setRespLoading(true);
            try {
                const sanitizedContactNumber = (contactNumber || "").replace(/-/g, "");
                const incidentResponse = await raiseIncident(incidentText, sanitizedContactNumber, incidentFiles);
                setIncidentResponse(incidentResponse);
            } catch (e) {
                console.error("Something went wrong. Please try again later.", e);
            } finally {
                setRespLoading(false);
                setIncidentFiles([]);
            }
        };
        createIncidentApi();
    }, []);

    return (
        <div className="ticket-container">
            <div className="ticket-desc">{t("chatBot.quickActions.state.ticketDescription")}</div>
            <div className="last-message">
                {typeof lastMessageContent === "string" ? lastMessageContent : Array.isArray(lastMessageContent) ? lastMessageContent.map((msg: any) => msg.text || "").join(" ") : ""}
            </div>
            <CreateIncidentResponse
                incidentResponse={incidentResponse}
                confirmTicketCreation={true}
                respLoading={respLoading}
            />
        </div>
    );
};

const ChatWithServiceDesk: React.FC<{ data: any }> = ({
    data
}) => {
    const { incidentText, isIncidentTextPending } = data;
    const [disabled, setDisabled] = useState<boolean>(false);
    const [incText, setIncText] = useState<string>(incidentText || "");
    const { setShowLiveAgentChat } = useSearchStore();
    const { actualMessages } = useContext(AiAssistContext);
    const feedback = useFeedback();
    const { company, currentRole } = useUserData();

    const composed = (...args: any) => {
        if (args[0].nextNodeId === "confirmChatWithServiceDesk") {
            setShowLiveAgentChat(true);
            feedback({
                source: "live-chat",
                messages: actualMessages,
                short_description: "MyHelp - " + incidentText,
                query: "MyHelp - Start live chat",
                company: company || "unknown",
                role: currentRole || "unknown",
            });
        }
        setDisabled(true);
        data.callback?.(...args);
    };

    const setIncTextFunc = (value: string) => {
        setIncText(value);
        data.incidentText = value;
        data.setIncidentText?.(value);
        data.options[0].disabled = !value.length;
    }

    useEffect(() => {
        setIncText(incidentText || "");
    }, [incidentText]);

    return isIncidentTextPending ? (
        <EonUiPreloaderSquares className="preloader" />
    ) : (
        <>
            <CreateIncidentInput
                isEditIncident={true}
                disabled={disabled}
                setIncTextFunc={setIncTextFunc}
                incidentText={incText}
                isIncidentTextPending={isIncidentTextPending}
            />
            <Options data={{ ...data, callback: composed }} isButton={true} />
        </>
    );
};

const RequestCallbackPrompt: React.FC<{ data: any }> = ({
    data
}) => {
    const { incidentText, isIncidentTextPending } = data;
    const [incText, setIncText] = useState<string>("");
    const [disabled, setDisabled] = useState<boolean>(false);

    const handleSetIncidentText = (value: string) => {
        setIncText(value);
        data.incidentText = value;
        data.setIncidentText?.(value);
        data.options[0].disabled = !value.length;
    };

    const composed = (...args: any) => {
        setDisabled(true);
        data.callback?.(...args);
    };

    useEffect(() => {
        if (incidentText) {
            setIncText(incidentText);
        }
    }, []);

    useEffect(() => {
        setIncText(incidentText || "");
    }, [incidentText]);

    return (
        <>
            {isIncidentTextPending ? (
                <EonUiPreloaderSquares
                    className="preloader"
                />
            ) : (
                <>
                    <ExpandingInput
                        value={incText}
                        onValueChanged={(e) =>
                            handleSetIncidentText(e.target.value)
                        }
                        disabled={disabled}
                    />
                    <Options data={{ ...data, callback: composed }} isButton={true} />
                </>
            )}
        </>
    );
};

export const QuickActions: React.FC<MessageWithActionAndData> = ({
    data,
    content,
}) => {

    const t = useTranslation();
    const contentString = typeof content === "string" ? content : (Array.isArray(content) && content.length > 0 && typeof content[0] === "object" && "text" in content[0] ? content[0].text : "");
    const renderStep = (data: DecisionNode): any => {
        switch (data.type) {
        case "options":
            return <Options data={data} isButton={true} />;
        case "create-incident":
            return <CreateIncident data={data} />;
        case "chat-with-service-desk":
            return <ChatWithServiceDesk data={data} />;
        case "create-incident-response":
            return <CreateIncidentResult data={data} />;
        case "request-callback-prompt":
            return <RequestCallbackPrompt data={data} />;
        case "request-callback":
            return <RequestCallback data={data} />;
        case "request-callback-result":
            return <RequestCallbackResult data={data} />;
        case "text":
        default:
            return <></>;
        }
    };

    return (
        <div className="bot-header">
            <div className="bot-reply-container">
                {contentString && <TypingEffect className="bot-text" text={t(contentString)} speed={1} />}
                {renderStep(data)}
            </div>
        </div>
    );
};

export const handleAiQueryNew = (history: Message[], query: string, quickActionType: QuickActionType, data: any) => {
    const lastMessageContent = history[history.length - 1]?.content;
    const category = lastMessageContent ? "ongoing" : "new";
    let findNode = getDecisionTree().find(node => node.userCommand === query && (!data?.nodeId || data?.nodeId === node.id) && (!node.condition || node.condition({
        category,
        quickActionType
    })));
    findNode = { ...findNode, ...data || {} };
    return getFrom(AiActionByCustomType)("quick-actions")(findNode);
}